// @ts-strict-ignore
import { Dictionary } from 'typescript-collections';
import { ConnectionAreaSearchQueryVO } from '../components/article/model/vo/connection-area-search-query.vo';
import { Plane, Vector3 } from 'three';
import { ConnectionCubeSearchVector } from '@article/components/connections/connection-creation/search/connection-cube-search-vector';
import { ConnectionCubeSearchRequirements } from '@article/components/connections/connection-creation/search/connection-cube-search-requirements';
import { ArrayUtils } from '@shared/util/array-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { PropertyVO } from '../components/article/model/vo/property.vo';
import { ConnectionCubeSearchHelper } from '@article/components/connections/connection-creation/search/connection-cube-search.helper';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { ConnectionAreaSearchRequirementsVO } from '../components/article/model/vo/connection-area-search-requirements.vo';
import { ConnectionAreaSearchVectorVO } from '../components/article/model/vo/connection-area-search-vector.vo';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { GroupedCubesSearchRequirements } from '../model/vo/grouped-cubes-search-requirements';
import { PropertyUtils } from '@article/util/property-utils';
import { SearchQueryType } from '../components/article/model/vo/search-query-type';

export class SearchQueryBuilder {

  private static readonly EPSILON: number = 0.08;

  private static readonly X_AXIS: string = 'x';
  private static readonly Y_AXIS: string = 'y';
  private static readonly Z_AXIS: string = 'z';

  private readonly _connectionIdToMiddlePoint: Dictionary<string, Vector3> = new Dictionary<string, Vector3>();
  private _connectionCubeVectors: ConnectionCubeSearchVector[] = [];
  private _searchByArticles: boolean = false;
  private _searchForMultiplePlacementsOfResultsInSingleSearchVectorEnabled: boolean = false;

  private _searchType: string = SearchQueryType.ACCESSORY_ARTICLE;

  private _searchRequirements: ConnectionAreaSearchRequirementsVO[] = [];
  private _connectionAreaSearchVectors: ConnectionAreaSearchVectorVO[] = [];
  private _includedArticleTypes: string[] = undefined;
  private _electedArticleIds: number[] = undefined;

  constructor(private connectionCubeSearchHelper: ConnectionCubeSearchHelper) {}

  get electedArticleIds(): number[] {
    return this._electedArticleIds;
  }

  set electedArticleIds(value: number[]) {
    this._electedArticleIds = value;
  }

  get includedArticleTypes(): string[] {
    return this._includedArticleTypes;
  }

  set includedArticleTypes(value: string[]) {
    this._includedArticleTypes = value;
  }

  public get connectionCubeVectors(): ConnectionCubeSearchVector[] {
    return this._connectionCubeVectors;
  }

  public get searchByArticles(): boolean {
    return this._searchByArticles;
  }

  public set searchByArticles(searchByArticles: boolean) {
    this._searchByArticles = searchByArticles;
  }

  public get searchForMultiplePlacementsOfResultsInSingleSearchVectorEnabled(): boolean {
    return this._searchForMultiplePlacementsOfResultsInSingleSearchVectorEnabled;
  }

  public set searchForMultiplePlacementsOfResultsInSingleSearchVectorEnabled(value: boolean) {
    this._searchForMultiplePlacementsOfResultsInSingleSearchVectorEnabled = value;
  }

  public get searchType(): string {
    return this._searchType;
  }

  public set searchType(value: string) {
    this._searchType = value;
  }

  public prepareAcceptingConnections(
    acceptingCubesSearchRequirements: ConnectionCubeSearchRequirements[],
    includeMultipleCommonVector: boolean = true,
    existingCubeVectors: ConnectionCubeSearchVector[] = null
  ): SearchQueryBuilder {

    this._connectionCubeVectors = [];
    this._searchRequirements = [];
    this._connectionAreaSearchVectors = [];

    let requirementIndex: number = -1;
    const cubeIdToRequirementIndex: Dictionary<string, number> = new Dictionary<string, number>();
    const sortedCubesRequirements: ConnectionCubeSearchRequirements[] = [];
    const nonFreedomCubesRequirements: ConnectionCubeSearchRequirements[] = [];

    const cubeVectors: ConnectionCubeSearchVector[] = existingCubeVectors
      ? existingCubeVectors
      : this.createCubeVectorsBySearchRequirements(acceptingCubesSearchRequirements);
    for (const cubeVector of cubeVectors) {
      let includeVector: boolean = this.connectionCubeSearchHelper.validateVectorToConnectArticle(cubeVector);
      for (const cubeRequirements of cubeVector.sortedCubesSearchRequirements) {
        if (!cubeIdToRequirementIndex.containsKey(cubeRequirements.cubeUID)) {
          const connectionSearchRequirements: ConnectionAreaSearchRequirementsVO = new ConnectionAreaSearchRequirementsVO();
          connectionSearchRequirements.connectionRequirements = cubeRequirements.connectionRequirements;
          connectionSearchRequirements.articleId = cubeRequirements.articleId;
          if (connectionSearchRequirements.connectionRequirements.length > 0) {
            this._searchRequirements[++requirementIndex] = connectionSearchRequirements;
            cubeIdToRequirementIndex.setValue(cubeRequirements.cubeUID, requirementIndex);
            sortedCubesRequirements.push(cubeRequirements);
            if (!cubeVector.searchForFreedomConnectionTarget) {
              nonFreedomCubesRequirements.push(cubeRequirements);
            }
          } else {
            includeVector = false;
          }
        }
        // update requirement index regarding created sort order of all requirements
        cubeRequirements.globalRequirementIndex = cubeIdToRequirementIndex.getValue(cubeRequirements.cubeUID);
      }

      if (includeVector) {
        const connectionAreaVector: ConnectionAreaSearchVectorVO = this.createConnectionAreaSearchVector(cubeVector, cubeIdToRequirementIndex);
        this.addConnectionAreaSearchVector(connectionAreaVector);
        cubeVector.id = connectionAreaVector.id;
        this._connectionCubeVectors.push(cubeVector);
      }
    }

    // add common vector to search articles with single giving connection
    const singleCommonCubeVector: ConnectionCubeSearchVector = this.createConnectionCubeVector(sortedCubesRequirements, false);
    const singleCommonConnectionAreaVector: ConnectionAreaSearchVectorVO = this.createConnectionAreaSearchVector(
      singleCommonCubeVector,
      cubeIdToRequirementIndex
    );
    // add common search vector at the start of connectionAreaSearchVectors array
    this.addConnectionAreaSearchVector(singleCommonConnectionAreaVector, 0);
    singleCommonCubeVector.id = singleCommonConnectionAreaVector.id;
    this._connectionCubeVectors.push(singleCommonCubeVector);

    if (includeMultipleCommonVector) {
      // add common vector to search articles with multiple giving connections
      // range-on-line connections should not be included in it
      const multipleCommonCubeVector: ConnectionCubeSearchVector = this.createConnectionCubeVector(nonFreedomCubesRequirements, true);
      if (
        this.connectionCubeSearchHelper.validateVectorToConnectArticle(multipleCommonCubeVector, true) &&
        !this.isCubeSearchVectorExists(multipleCommonCubeVector, this._connectionCubeVectors)
      ) {
        const multipleCommonConnectionAreaVector: ConnectionAreaSearchVectorVO = this.createConnectionAreaSearchVector(
          multipleCommonCubeVector,
          cubeIdToRequirementIndex
        );
        this.addConnectionAreaSearchVector(multipleCommonConnectionAreaVector, 1);
        multipleCommonCubeVector.id = multipleCommonConnectionAreaVector.id;
        this._connectionCubeVectors.push(multipleCommonCubeVector);
      }
    }

    return this;
  }

  private addConnectionAreaSearchVector(
    connectionAreaSearchVector: ConnectionAreaSearchVectorVO,
    insertIndex: number = -1
  ): void {
    connectionAreaSearchVector.id = this._connectionAreaSearchVectors.length;
    if (insertIndex === -1) {
      this._connectionAreaSearchVectors.push(connectionAreaSearchVector);
    } else {
      this._connectionAreaSearchVectors.splice(insertIndex, 0, connectionAreaSearchVector);
    }
  }

  private createConnectionAreaSearchVector(
    cubeVector: ConnectionCubeSearchVector,
    cubeIdToRequirementIndex: Dictionary<string, number>
  ): ConnectionAreaSearchVectorVO {
    const searchVector: ConnectionAreaSearchVectorVO = new ConnectionAreaSearchVectorVO();
    searchVector.searchForMultipleGivingConnections = cubeVector.searchForMultipleGivingConnections;
    searchVector.searchForFreedomConnectionTarget = cubeVector.searchForFreedomConnectionTarget;
    searchVector.globalRequirementIndices = this.createRequirementIndices(cubeVector.sortedCubesSearchRequirements, cubeIdToRequirementIndex);
    searchVector.additionalPropertiesMap = this.createAdditionalPropertiesMap(cubeVector.additionalPropertiesMap, cubeIdToRequirementIndex);
    searchVector.excludedArticleTypes = this.checkAdditionalExcludedTypesByVector(cubeVector);
    return searchVector;
  }

  private checkAdditionalExcludedTypesByVector(cubeVector: ConnectionCubeSearchVector): string[] {
    if (!cubeVector.searchForMultipleGivingConnections) {
      return null;
    }
    const invalidArticleTypes: string[] = [];
    if (!this.connectionCubeSearchHelper.validateVectorToConnectBridge(cubeVector, false)) {
      invalidArticleTypes.push(ArticleTypeConstants.TYPE_BRIDGE);
    }
    return invalidArticleTypes.length > 0 ? invalidArticleTypes : null;
  }

  private createAdditionalPropertiesMap(
    cubeIdToRequirementsMap: Dictionary<string, PropertyVO[]>,
    cubeIdToRequirementIndex: Dictionary<string, number>
  ): any {
    if (!cubeIdToRequirementsMap) {
      return null;
    }
    const propertiesMap: any = {};
    for (const cubeUid of cubeIdToRequirementsMap.keys()) {
      if (!cubeIdToRequirementIndex.containsKey(cubeUid)) {
        continue;
      }
      const requirementIndex: number = cubeIdToRequirementIndex.getValue(cubeUid);
      this.addAdditionalPropertiesByIndex(cubeIdToRequirementsMap.getValue(cubeUid), propertiesMap, requirementIndex);
    }
    return propertiesMap;
  }

  private addAdditionalPropertiesByIndex(properties: PropertyVO[], additionalPropertiesMap: {}, requirementIndex: number): void {
    if (!properties || properties.length === 0 || !additionalPropertiesMap) {
      return;
    }
    let existingAdditionalProperties: PropertyVO[] = additionalPropertiesMap[requirementIndex.toString()];
    if (!existingAdditionalProperties) {
      existingAdditionalProperties = [];
    }
    ArrayUtils.addAll(existingAdditionalProperties, properties);
    additionalPropertiesMap[requirementIndex.toString()] = existingAdditionalProperties;
  }

  private createRequirementIndices(
    sortedCubesSearchRequirements: ConnectionCubeSearchRequirements[],
    cubeIdToRequirementIndex: Dictionary<string, number>
  ): number[] {
    const requirementIndexes: number[] = [];
    for (const cubeSearchRequirements of sortedCubesSearchRequirements) {
      if (!cubeIdToRequirementIndex.containsKey(cubeSearchRequirements.cubeUID)) {
        continue;
      }
      requirementIndexes.push(cubeIdToRequirementIndex.getValue(cubeSearchRequirements.cubeUID));
    }
    return requirementIndexes;
  }

  private createCubeVectorsBySearchRequirements(acceptingCubesSearchRequirements: ConnectionCubeSearchRequirements[]): ConnectionCubeSearchVector[] {
    const rangeOnLineSearchRequirements: ConnectionCubeSearchRequirements[] = [];
    // separate search-vectors should be created for each range-on-line (freedom) connection, so such connections should not be included
    // in vectors created along specific axis
    acceptingCubesSearchRequirements = this.excludeRangeOnLineCubeRequirements(acceptingCubesSearchRequirements, rangeOnLineSearchRequirements);
    let cubeVectors: ConnectionCubeSearchVector[] = this.createCubeVectorsByDirections(acceptingCubesSearchRequirements);
    if (rangeOnLineSearchRequirements.length > 0) {
      const rangeOnLineVectors: ConnectionCubeSearchVector[] = this.createRangeOnLineCubeVectors(rangeOnLineSearchRequirements);
      cubeVectors = cubeVectors.concat(rangeOnLineVectors);
    }
    return cubeVectors;
  }

  private createRangeOnLineCubeVectors(rangeOnLineSearchRequirements: ConnectionCubeSearchRequirements[]): ConnectionCubeSearchVector[] {
    const result: ConnectionCubeSearchVector[] = [];
    for (const cubeRequirements of rangeOnLineSearchRequirements) {
      const cubeVector: ConnectionCubeSearchVector = this.constructRangeOnLineConnectionVector(cubeRequirements, true);
      if (cubeVector) {
        result.push(cubeVector);
      }
    }
    // sorting doesn't need to be applied to range-on-line vectors since they will be excluded from common search-vector
    // (to find articles with multiple giving connections)
    return result;
  }

  private constructRangeOnLineConnectionVector(
    cubeRequirements: ConnectionCubeSearchRequirements,
    searchForMultipleGivingConnections: boolean
  ): ConnectionCubeSearchVector {
    const connectionCubesInVector: ConnectionCubeSearchRequirements[] = [];
    connectionCubesInVector.push(cubeRequirements);
    const cubeVector: ConnectionCubeSearchVector = this.createConnectionCubeVector(connectionCubesInVector, searchForMultipleGivingConnections);
    if (cubeVector) {
      cubeVector.searchForFreedomConnectionTarget = true;
    }
    return cubeVector;
  }

  private createConnectionCubeVector(
    sortedCubesRequirements: ConnectionCubeSearchRequirements[],
    searchForMultipleGivingConnections: boolean,
    directionAxis: string = null,
    equalAxis: string = null
  ): ConnectionCubeSearchVector {
    const cubeSearchVector: ConnectionCubeSearchVector = new ConnectionCubeSearchVector(
      -1,
      sortedCubesRequirements,
      searchForMultipleGivingConnections,
      directionAxis,
      equalAxis
    );
    if (searchForMultipleGivingConnections) {
      cubeSearchVector.additionalPropertiesMap = this.calculateAdditionalCubeRequirements(sortedCubesRequirements);
    }
    return cubeSearchVector;
  }

  private calculateAdditionalCubeRequirements(sortedCubeRequirements: ConnectionCubeSearchRequirements[]): Dictionary<string, PropertyVO[]> {
    const propertiesMap: Dictionary<string, PropertyVO[]> = new Dictionary<string, PropertyVO[]>();

    let lastDistanceProperty: PropertyVO = null;
    let cube: Connection = null;
    for (let i: number = 0; i < sortedCubeRequirements.length; i++) {
      if (!cube) {
        cube = (sortedCubeRequirements[i] as ConnectionCubeSearchRequirements).getConnectionCube();
      }
      if (i < sortedCubeRequirements.length - 1) {
        // calculate distance only for connection that have further neighbor connection
        const nextCube: Connection = (sortedCubeRequirements[i + 1] as ConnectionCubeSearchRequirements).getConnectionCube();
        const distance: number = this.getConnectionMiddlePointByCube(cube).distanceTo(this.getConnectionMiddlePointByCube(nextCube));
        const distanceProperty: PropertyVO = PropertyUtils.createDistanceProperty(distance);
        this.addAdditionalPropertyByCubeUid(distanceProperty, propertiesMap, cube.uid);

        lastDistanceProperty = distanceProperty;
        cube = nextCube;
      } else {
        // process last connection without neighbor
        if (lastDistanceProperty) {
          this.addAdditionalPropertyByCubeUid(lastDistanceProperty, propertiesMap, cube.uid);
        }
      }
    }

    return propertiesMap;
  }

  private addAdditionalPropertyByCubeUid(property: PropertyVO, additionalPropertiesMap: Dictionary<string, PropertyVO[]>, cubeUID: string): void {
    if (!property || !additionalPropertiesMap) {
      return;
    }
    let existingAdditionalProperties: PropertyVO[] = additionalPropertiesMap.getValue(cubeUID);
    if (!existingAdditionalProperties) {
      existingAdditionalProperties = [];
    }
    existingAdditionalProperties.push(property);
    additionalPropertiesMap.setValue(cubeUID, existingAdditionalProperties);
  }

  private excludeRangeOnLineCubeRequirements(
    cubesRequirements: ConnectionCubeSearchRequirements[],
    excludedRangeOnLineRequirements: ConnectionCubeSearchRequirements[] = null
  ): ConnectionCubeSearchRequirements[] {
    const filteredRequirements: ConnectionCubeSearchRequirements[] = [];
    for (const cubeSearchRequirements of cubesRequirements) {
      if (!cubeSearchRequirements.isRangeOnLineConnection()) {
        filteredRequirements.push(cubeSearchRequirements);
      } else {
        if (excludedRangeOnLineRequirements && excludedRangeOnLineRequirements.indexOf(cubeSearchRequirements) < 0) {
          excludedRangeOnLineRequirements.push(cubeSearchRequirements);
        }
      }
    }
    return filteredRequirements;
  }

  public createCubeVectorsByDirections(acceptingCubesSearchRequirements: ConnectionCubeSearchRequirements[]): ConnectionCubeSearchVector[] {
    // note: we should create vectors along z-axis first to keep correct order of connections in the "main" vector with all
    // accepting connections
    const vectorsAlongZAxis: ConnectionCubeSearchVector[] = this.createConnectionVectorsAlongZAxis(acceptingCubesSearchRequirements);

    const vectorsAlongXAxis: ConnectionCubeSearchVector[] = this.createConnectionVectorsAlongXAxis(acceptingCubesSearchRequirements);
    this.addSearchVectors(vectorsAlongXAxis, vectorsAlongZAxis);

    return vectorsAlongZAxis;
  }

  public addSearchVectors(searchVectors: ConnectionCubeSearchVector[], source: ConnectionCubeSearchVector[]): void {
    for (const cubeVector of searchVectors) {
      if (!this.isCubeSearchVectorExists(cubeVector, source)) {
        source.push(cubeVector);
      }
    }
  }

  private isCubeSearchVectorExists(cubeVector: ConnectionCubeSearchVector, sourceVectors: ConnectionCubeSearchVector[]): boolean {
    for (const sourceCubeVector of sourceVectors) {
      if (this.connectionCubeSearchHelper.equalsConnectionCubeSearchVectors(cubeVector, sourceCubeVector)) {
        return true;
      }
    }
    return false;
  }

  private createConnectionVectorsAlongXAxis(acceptingCubesSearchRequirements: ConnectionCubeSearchRequirements[]): ConnectionCubeSearchVector[] {
    return this.createVectorsAlongAxis(acceptingCubesSearchRequirements, SearchQueryBuilder.X_AXIS, SearchQueryBuilder.Z_AXIS);
  }

  private createConnectionVectorsAlongZAxis(acceptingCubesSearchRequirements: ConnectionCubeSearchRequirements[]): ConnectionCubeSearchVector[] {
    return this.createVectorsAlongAxis(acceptingCubesSearchRequirements, SearchQueryBuilder.Z_AXIS, SearchQueryBuilder.X_AXIS);
  }

  public createVectorsAlongAxis(
    acceptingCubesSearchRequirements: ConnectionCubeSearchRequirements[],
    directionAxis: string,
    equalAxis: string
  ): ConnectionCubeSearchVector[] {
    const connectionVectors: ConnectionCubeSearchVector[] = [];
    let processedCubeIds: string[] = [];
    let sourceSearchRequirements: ConnectionCubeSearchRequirements[] = acceptingCubesSearchRequirements.concat();
    for (const cubeRequirements of acceptingCubesSearchRequirements) {
      if (processedCubeIds.indexOf(cubeRequirements.cubeUID) >= 0) {
        continue;
      }
      const connectionVector: ConnectionCubeSearchVector = this.constructConnectionVector(
        cubeRequirements,
        sourceSearchRequirements,
        directionAxis,
        equalAxis
      );
      connectionVectors.push(connectionVector);

      sourceSearchRequirements = sourceSearchRequirements.filter(ArrayUtils.notInArray, connectionVector.sortedCubesSearchRequirements);
      processedCubeIds = processedCubeIds.concat(connectionVector.getConnectionCubeIds());
    }
    connectionVectors.sort((a: ConnectionCubeSearchVector, b: ConnectionCubeSearchVector) => this.cubeVectorsSortFunction(a, b, equalAxis));
    return connectionVectors;
  }

  private getConnectionMiddlePoint(cubeSearchRequirements: ConnectionCubeSearchRequirements): Vector3 {
    return this._connectionIdToMiddlePoint.containsKey(cubeSearchRequirements.cubeUID)
      ? this._connectionIdToMiddlePoint.getValue(cubeSearchRequirements.cubeUID)
      : this.getConnectionMiddlePointByCube(cubeSearchRequirements.getConnectionCube());
  }

  private getConnectionMiddlePointByCube(cube: Connection): Vector3 {
    if (!this._connectionIdToMiddlePoint.containsKey(cube.uid)) {
      this._connectionIdToMiddlePoint.setValue(cube.uid, MathUtils.calculateMiddlePointByRectangle(cube.compositionRectangle));
    }
    return this._connectionIdToMiddlePoint.getValue(cube.uid);
  }

  public connectionCubesSortFunction(a: ConnectionCubeSearchRequirements, b: ConnectionCubeSearchRequirements, sortAxis: string): number {
    const aMiddlePoint: Vector3 = this.getConnectionMiddlePoint(a);
    const bMiddlePoint: Vector3 = this.getConnectionMiddlePoint(b);
    if ('x' === sortAxis) {
      return MathUtils.compare(bMiddlePoint[sortAxis], aMiddlePoint[sortAxis]);
    } else {
      return MathUtils.compare(aMiddlePoint[sortAxis], bMiddlePoint[sortAxis]);
    }
  }

  private cubeVectorsSortFunction(a: ConnectionCubeSearchVector, b: ConnectionCubeSearchVector, sortAxis: string): number {
    const aCubeRequirements: ConnectionCubeSearchRequirements = a.sortedCubesSearchRequirements[0];
    const bCubeRequirements: ConnectionCubeSearchRequirements = b.sortedCubesSearchRequirements[0];
    const aMiddlePoint: Vector3 = this.getConnectionMiddlePoint(aCubeRequirements);
    const bMiddlePoint: Vector3 = this.getConnectionMiddlePoint(bCubeRequirements);
    if ('x' === sortAxis) {
      return MathUtils.compare(bMiddlePoint[sortAxis], aMiddlePoint[sortAxis]);
    } else {
      return MathUtils.compare(aMiddlePoint[sortAxis], bMiddlePoint[sortAxis]);
    }
  }

  private constructConnectionVector(
    targetCubeRequirements: ConnectionCubeSearchRequirements,
    sourceCubesSearchRequirements: ConnectionCubeSearchRequirements[],
    directionAxis: string,
    equalAxis: string
  ): ConnectionCubeSearchVector {
    const connectionCubesInVector: ConnectionCubeSearchRequirements[] = [];
    const targetMiddlePoint: Vector3 = this.getConnectionMiddlePoint(targetCubeRequirements);
    let sourceMiddlePoint: Vector3 = null;
    for (const cubeRequirements of sourceCubesSearchRequirements) {
      sourceMiddlePoint = this.getConnectionMiddlePoint(cubeRequirements);
      if (MathUtils.compare(targetMiddlePoint[equalAxis], sourceMiddlePoint[equalAxis], SearchQueryBuilder.EPSILON) === 0) {
        connectionCubesInVector.push(cubeRequirements);
      }
    }
    let sortAxis: string = directionAxis;
    // support case when connections are aligned vertically (along y-axis)
    if (this.isVerticallyAlignedConnections(connectionCubesInVector)) {
      sortAxis = SearchQueryBuilder.Y_AXIS;
    }
    connectionCubesInVector.sort((a: ConnectionCubeSearchRequirements, b: ConnectionCubeSearchRequirements) =>
      this.connectionCubesSortFunction(a, b, sortAxis)
    );
    return this.createConnectionCubeVector(connectionCubesInVector, true, directionAxis, equalAxis);
  }

  private isVerticallyAlignedConnections(connectionCubesInVector: ConnectionCubeSearchRequirements[]): boolean {
    if (connectionCubesInVector.length < 2) {
      return false;
    }
    const targetRequirements: ConnectionCubeSearchRequirements = connectionCubesInVector[0];
    const targetRectangle: Rectangle3D = targetRequirements.getConnectionRectangle();
    if (!targetRectangle) {
      return false;
    }
    const targetNormal: Vector3 = MathUtils.getNormalFromFirstPoint(targetRectangle.point1, targetRectangle.point2, targetRectangle.point3);
    if (this.isVerticallyAlignedNormal(targetNormal)) {
      return false;
    }
    const targetPlane: Plane = new Plane();
    targetPlane.setFromCoplanarPoints(targetRectangle.point1, targetRectangle.point2, targetRectangle.point3);
    const targetProjectionVector: Vector3 = this.getConnectionMiddlePoint(targetRequirements);

    for (let i: number = 1; i < connectionCubesInVector.length; i++) {
      if (!this.isVerticallyAlignedConnection(connectionCubesInVector[i], targetNormal, targetPlane, targetProjectionVector)) {
        return false;
      }
    }
    return true;
  }

  private isVerticallyAlignedNormal(normal: Vector3): boolean {
    if (!normal) {
      return false;
    }
    const dotProduct: number = normal.dot(new Vector3(0, 1, 0));
    return MathUtils.compare(dotProduct, 0) !== 0;
  }

  private isVerticallyAlignedConnection(
    connectionRequirements: ConnectionCubeSearchRequirements,
    targetNormal: Vector3,
    targetPlane: Plane,
    targetProjectionVector: Vector3
  ): boolean {
    if (!connectionRequirements || !connectionRequirements.getConnectionRectangle()) {
      return false;
    }
    const connectionRectangle: Rectangle3D = connectionRequirements.getConnectionRectangle();
    const connectionNormal: Vector3 = MathUtils.getNormalFromFirstPoint(
      connectionRectangle.point1,
      connectionRectangle.point2,
      connectionRectangle.point3
    );
    if (!MathUtils.equalsNumber3D(connectionNormal, targetNormal)) {
      return false;
    }

    const projectionVector: Vector3 = targetPlane.projectPoint(this.getConnectionMiddlePoint(connectionRequirements), new Vector3());
    return MathUtils.isEqualsVectorsByXZAxis(targetProjectionVector, projectionVector);
  }

  public prepareGroupedConnections(
    groupedCubesSearchRequirements: GroupedCubesSearchRequirements[],
    includeMultipleCommonVector: boolean = true
  ): SearchQueryBuilder {
    const allCubeVectors: ConnectionCubeSearchVector[] = [];
    let cubeVectors: ConnectionCubeSearchVector[];
    for (const groupedRequirements of groupedCubesSearchRequirements) {
      cubeVectors = this.createCubeVectorsBySearchRequirements(groupedRequirements.searchRequirements);
      if (cubeVectors && cubeVectors.length > 0) {
        this.addSearchVectors(cubeVectors, allCubeVectors);
      }
    }
    return this.prepareAcceptingConnections(null, includeMultipleCommonVector, allCubeVectors);
  }

  public buildQuery(): ConnectionAreaSearchQueryVO {
    const connectionAreaSearchQuery: ConnectionAreaSearchQueryVO = new ConnectionAreaSearchQueryVO();
    connectionAreaSearchQuery.searchByArticles = this._searchByArticles;
    connectionAreaSearchQuery.isSearchForMultiplePlacementsOfResultsInSingleSearchVectorEnabled =
      this._searchForMultiplePlacementsOfResultsInSingleSearchVectorEnabled;
    connectionAreaSearchQuery.requirements = this._searchRequirements;
    connectionAreaSearchQuery.searchVectors = this._connectionAreaSearchVectors;
    connectionAreaSearchQuery.searchType = this._searchType;
    connectionAreaSearchQuery.includedArticleTypes = this._includedArticleTypes;
    connectionAreaSearchQuery.electedArticleIds = this._electedArticleIds;
    return connectionAreaSearchQuery;
  }
}
